import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { pageCache } from '../utils/pageCache';
import { Clock, Coffee, Sun, Users, AlertTriangle, Printer } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot,
} from 'recharts';
import { fmt, fmtK, fmtCount, fmtPct } from '../utils/formatters';
import LoadingScreen from '../components/LoadingScreen';
import DataEndsNote from '../components/DataEndsNote';
import Change from '../components/Change';

function KpiCard({ label, value, change, isMoney = true }: {
  label: string; value: number | string; change: number | null; isMoney?: boolean;
}) {
  const showChange = typeof change === 'number' && change !== 0;
  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(74,45,94,0.08)] transition-all duration-300">
      <div className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">{label}</div>
      <div className="text-2xl font-sans font-bold text-[#2D2A2E] tabular-nums">
        {typeof value !== 'number' ? value : isMoney ? fmt(value) : fmtCount(value)}
      </div>
      {showChange && (
        <div className="mt-1">
          <Change value={change} />
        </div>
      )}
    </div>
  );
}

function getHeatColor(value: number, max: number): string {
  if (max === 0 || value === 0) return '#F5F3F0';
  const intensity = Math.min(value / max, 1);
  const colors = [
    { r: 245, g: 243, b: 240 },
    { r: 220, g: 210, b: 230 },
    { r: 180, g: 160, b: 200 },
    { r: 120, g: 90, b: 150 },
    { r: 74, g: 45, b: 94 },
  ];
  const idx = intensity * (colors.length - 1);
  const lower = Math.floor(idx);
  const upper = Math.min(lower + 1, colors.length - 1);
  const t = idx - lower;
  const r = Math.round(colors[lower].r + (colors[upper].r - colors[lower].r) * t);
  const g = Math.round(colors[lower].g + (colors[upper].g - colors[lower].g) * t);
  const b = Math.round(colors[lower].b + (colors[upper].b - colors[lower].b) * t);
  return `rgb(${r},${g},${b})`;
}

function RevenueHeatmap({ data }: { data: any[] }) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 15 }, (_, i) => i + 6);
  const maxRevenue = Math.max(...data.map((d: any) => d.revenue), 1);
  const getCell = (day: string, hour: number) =>
    data.find((d: any) => d.day === day && d.hour === hour) || { revenue: 0 };

  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card mb-8">
      <h3 className="font-serif text-lg text-[#2D2A2E] mb-4">Revenue Heatmap</h3>
      <div className="overflow-x-auto">
        <div className="min-w-[700px]">
          <div className="flex mb-1">
            <div className="w-12 shrink-0" />
            {hours.map((h) => (
              <div key={h} className="flex-1 text-center text-[10px] font-sans text-[#6B6670] tabular-nums">{h}:00</div>
            ))}
          </div>
          {days.map((day) => (
            <div key={day} className="flex items-center mb-1">
              <div className="w-12 shrink-0 text-xs font-sans text-[#6B6670] font-medium">{day}</div>
              {hours.map((hour) => {
                const cell = getCell(day, hour);
                const bg = getHeatColor(cell.revenue, maxRevenue);
                return (
                  <div key={hour} className="flex-1 mx-0.5 rounded-[4px] h-10 cursor-default transition-all hover:scale-105 group relative" style={{ backgroundColor: bg }}>
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-[#2D2A2E] text-white text-[10px] font-sans px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 tabular-nums">
                      {day} {hour}:00 — {fmt(cell.revenue)}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div className="flex items-center justify-end gap-2 mt-4">
            <span className="text-[10px] font-sans text-[#6B6670]">Low</span>
            <div className="flex gap-0.5">
              {[0, 0.25, 0.5, 0.75, 1].map((intensity) => (
                <div key={intensity} className="w-6 h-3 rounded-sm" style={{ backgroundColor: getHeatColor(intensity * maxRevenue, maxRevenue) }} />
              ))}
            </div>
            <span className="text-[10px] font-sans text-[#6B6670]">High</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const DAYPART_COLORS = ['#4A2D5E', '#2E6B4F', '#7BA68E', '#B54A4A', '#6B6670'];
const DAYPART_ICONS: Record<string, React.ReactNode> = {
  'Early Bird': <Coffee size={14} strokeWidth={1.5} />,
  'Morning Rush': <Sun size={14} strokeWidth={1.5} />,
  'Lunch Crowd': <Users size={14} strokeWidth={1.5} />,
  'Afternoon Lull': <Clock size={14} strokeWidth={1.5} />,
  'Evening Wind-down': <Clock size={14} strokeWidth={1.5} />,
};

export default function TimeAnalysisPage() {
  const [data, setData] = useState<any>(() => pageCache.get('/api/time-analysis?period=month') ?? null);
  const [loading, setLoading] = useState(() => !pageCache.has('/api/time-analysis?period=month'));
  const [period, setPeriod] = useState('month');
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const url = `/api/time-analysis?period=${period}`;
    if (pageCache.has(url)) {
      setData(pageCache.get(url));
      setLoading(false);
    }
    axios.get(url)
      .then((res) => { pageCache.set(url, res.data); setData(res.data); })
      .catch(() => { if (!pageCache.has(url)) setData(null); })
      .finally(() => setLoading(false));
  }, [period]);

  const hasData = data && data.daily_trend && data.daily_trend.length > 0;
  // "Orders" only when every line carries an order number, else "Items sold"
  // (backend helpers.order_metrics); the daypart counts use the same unit.
  const countLabel: string = data?.kpis?.total_transactions?.label || 'Transactions';
  const maxDaypartRev = data?.daypart_profiles
    ? Math.max(...data.daypart_profiles.map((d: any) => d.revenue), 1)
    : 1;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl text-[#2D2A2E]">When your business makes money</h1>
          <DataEndsNote dataEnds={data?.data_ends} period={period} />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => window.print()} className="no-print inline-flex items-center gap-1.5 h-10 px-3 rounded-[8px] font-sans text-sm bg-white border border-[#E6E2DE] text-[#6B6670] hover:border-[#4A2D5E] transition-all">
            <Printer size={15} strokeWidth={1.5} />
            Export
          </button>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-10 font-sans text-sm px-3 outline-none w-44">
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">Last 90 Days</option>
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingScreen fullScreen={false} />
      ) : !hasData ? (
        <div className="flex flex-col items-center justify-center py-24">
          <Clock size={48} strokeWidth={1.5} className="text-[#4A2D5E]/30 mb-4" />
          <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">No time data yet</h3>
          <p className="font-sans text-sm text-[#6B6670] mb-6 text-center max-w-sm">Upload transaction data with dates to see your peak and slow period analysis.</p>
          <button onClick={() => navigate('/settings/data')} className="bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors">Upload Data</button>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <KpiCard label="Avg Daily Revenue" value={data.kpis.avg_daily_revenue.value} change={data.kpis.avg_daily_revenue.change} />
            <KpiCard label="Best Day Revenue" value={data.kpis.best_day_revenue.value} change={0} />
            <KpiCard label="Busiest Day" value={data.kpis.busiest_day.value} change={0} isMoney={false} />
            <KpiCard label={data.kpis.total_transactions.label || 'Total Transactions'} value={data.kpis.total_transactions.value} change={data.kpis.total_transactions.change} isMoney={false} />
          </div>

          {/* Daypart Profiles */}
          {data.daypart_profiles && data.daypart_profiles.length > 0 && (
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card mb-8">
              <h3 className="font-serif text-lg text-[#2D2A2E] mb-1">Customer Daypart Profiles</h3>
              <p className="font-sans text-sm text-[#6B6670] mb-6">Revenue patterns by time of day — not guesses, based on your actual data.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {data.daypart_profiles.map((dp: any, i: number) => {
                  const color = DAYPART_COLORS[i % DAYPART_COLORS.length];
                  const barWidth = maxDaypartRev > 0 ? (dp.revenue / maxDaypartRev) * 100 : 0;
                  return (
                    <div key={dp.name} className="border border-[#E6E2DE] rounded-[10px] p-4">
                      <div className="h-1 rounded-full bg-[#F2F0ED] mb-3 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${barWidth}%`, backgroundColor: color }} />
                      </div>
                      <div className="flex items-center gap-1.5 mb-0.5" style={{ color }}>
                        {DAYPART_ICONS[dp.name] || <Clock size={14} strokeWidth={1.5} />}
                        <span className="font-sans text-xs font-semibold">{dp.name}</span>
                      </div>
                      <div className="text-[10px] font-sans text-[#6B6670] mb-3">{dp.label}</div>
                      <div className="space-y-2">
                        <div>
                          <div className="text-[10px] font-sans text-[#6B6670] uppercase tracking-wide">Revenue</div>
                          <div className="font-sans text-sm font-bold text-[#2D2A2E] tabular-nums">{fmt(dp.revenue)}</div>
                        </div>
                        <div className="flex gap-4">
                          <div>
                            <div className="text-[10px] font-sans text-[#6B6670] uppercase tracking-wide">Share</div>
                            <div className="font-sans text-sm font-semibold tabular-nums" style={{ color }}>{fmtPct(dp.share)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] font-sans text-[#6B6670] uppercase tracking-wide">{countLabel}</div>
                            <div className="font-sans text-sm text-[#2D2A2E] tabular-nums">{fmtCount(dp.transactions)}</div>
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] font-sans text-[#6B6670] uppercase tracking-wide">Top Item</div>
                          <div className="font-sans text-xs text-[#2D2A2E] leading-tight truncate">{dp.top_product}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Staff Scheduling Hints */}
          {data.staff_hints && data.staff_hints.length > 0 && (
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Users size={18} strokeWidth={1.5} className="text-[#4A2D5E]" />
                <h3 className="font-serif text-lg text-[#2D2A2E]">Staff Scheduling Hints</h3>
              </div>
              <div className="space-y-3">
                {data.staff_hints.map((hint: any, i: number) => (
                  <div key={i} className={`flex items-start gap-3 p-4 rounded-[8px] ${
                    hint.type === 'peak' ? 'bg-[#2E6B4F]/5 border border-[#2E6B4F]/15'
                    : hint.type === 'slow' ? 'bg-[#B54A4A]/5 border border-[#B54A4A]/15'
                    : 'bg-[#4A2D5E]/5 border border-[#4A2D5E]/15'
                  }`}>
                    <AlertTriangle
                      size={16} strokeWidth={1.5}
                      className={`mt-0.5 flex-shrink-0 ${hint.type === 'peak' ? 'text-[#2E6B4F]' : hint.type === 'slow' ? 'text-[#B54A4A]' : 'text-[#4A2D5E]'}`}
                    />
                    <p className="font-sans text-sm text-[#2D2A2E]">{hint.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Revenue Heatmap */}
          {data.heatmap && data.heatmap.length > 0 && <RevenueHeatmap data={data.heatmap} />}

          {/* Daily Revenue Trend with Anomaly Dots */}
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg text-[#2D2A2E]">Daily Revenue</h3>
              {data.anomalies?.length > 0 && (
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-sans text-[#6B6670] uppercase tracking-wide">Anomalies detected</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-sans text-[#2E6B4F]"><span className="w-2 h-2 rounded-full bg-[#2E6B4F] inline-block" /> Spike</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-sans text-[#B54A4A]"><span className="w-2 h-2 rounded-full bg-[#B54A4A] inline-block" /> Dip</span>
                </div>
              )}
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={data.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DE" />
                <XAxis dataKey="date" tick={{ fill: '#6B6670', fontSize: 12, fontFamily: 'DM Sans' }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: '#6B6670', fontSize: 12, fontFamily: 'DM Sans' }} tickFormatter={(v) => fmtK(v)} />
                <Tooltip
                  contentStyle={{ borderRadius: '10px', border: '1px solid #E6E2DE', fontFamily: 'DM Sans', fontSize: 12 }}
                  formatter={(value: number) => [fmt(value), 'Revenue']}
                  labelFormatter={(l) => `Date: ${l}`}
                />
                <Line type="monotone" dataKey="revenue" stroke="#4A2D5E" strokeWidth={2} dot={{ fill: '#4A2D5E', r: 2 }} />
                {data.anomalies?.map((a: any) => (
                  <ReferenceDot key={a.date} x={a.date} y={a.revenue} r={6}
                    fill={a.type === 'spike' ? '#2E6B4F' : '#B54A4A'} stroke="white" strokeWidth={2}
                    label={{ value: a.type === 'spike' ? '▲' : '▼', position: 'top', fontSize: 10, fill: a.type === 'spike' ? '#2E6B4F' : '#B54A4A' }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Day of Week */}
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
            <h3 className="font-serif text-lg text-[#2D2A2E] mb-4">Average Revenue by Day of Week</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.day_of_week}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DE" />
                <XAxis dataKey="day" tick={{ fill: '#6B6670', fontSize: 12, fontFamily: 'DM Sans' }} tickFormatter={(v) => v.slice(0, 3)} />
                <YAxis tick={{ fill: '#6B6670', fontSize: 12, fontFamily: 'DM Sans' }} tickFormatter={(v) => fmtK(v)} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E6E2DE', fontFamily: 'DM Sans', fontSize: 12 }} formatter={(value: number) => [fmt(value), 'Avg Revenue']} />
                <Bar dataKey="avg_revenue" fill="#4A2D5E" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </motion.div>
  );
}
